import * as React from "react";
import { Film } from "lucide-react";
import { CarouselSection } from "@/v2/pages/Lens/components/CarouselSection";
import type { CondensedClip } from "./useCondensedGameClips";

/**
 * The key moments of a game, as a horizontal strip below the player. Clicking a
 * card asks the caller to take the game there.
 *
 * Padding and gaps are inline: Tailwind padding utilities resolve to 0 inside
 * `.v2` because of the global Bootstrap reset. The scroller carries its own
 * `overflow-x: auto` so the page body never scrolls sideways.
 */

const ORDINALS = ["", "1st", "2nd", "3rd", "4th", "5th"];

const periodLabel = (period: number | null) =>
  period != null && ORDINALS[period] ? `${ORDINALS[period]} Period` : "Period";

/**
 * A position in the game video, which is what a card's time reports and where
 * clicking it goes.
 *
 * This used to report the clip's offset in the condensed reel. That was left
 * behind when the strip stopped playing the reel and started driving the game
 * player, so a card read "at 0:18" while clicking it jumped to 51 minutes in.
 *
 * Games run past an hour, so the hour appears only when there is one.
 */
const gameTime = (seconds: number | null) => {
  if (seconds == null || !Number.isFinite(seconds)) return "";
  const whole = Math.max(0, Math.floor(seconds));
  const h = Math.floor(whole / 3600);
  const m = Math.floor((whole % 3600) / 60);
  const s = whole % 60;
  const mm = h > 0 ? String(m).padStart(2, "0") : String(m);
  return `${h > 0 ? `${h}:` : ""}${mm}:${String(s).padStart(2, "0")}`;
};

/** A clip's own length, which never reaches an hour. */
const clipLength = (seconds: number) => {
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
};

const EVENT_LABELS: Record<string, string> = {
  GOAL: "GOAL",
  FACE_OFF: "FACEOFF",
  SAVE_GLOVE: "GLOVE SAVE",
  SAVE_BLOCKER: "BLOCKER SAVE",
};

const eventLabel = (eventType: string) =>
  EVENT_LABELS[eventType] ?? String(eventType ?? "").replace(/_/g, " ");

/** The gap CarouselSection leaves between cards, which the connector spans. */
const CARD_GAP = 22.5;
/** Vertical centre of a card's index badge, from the card's top. */
const BADGE_CENTRE = 19;

/**
 * The game's two teams as the tenant knows them, which is the only place the
 * crests exist: the analytics side stores team ids and names and no imagery.
 * The TPE ids are what tie the two together.
 */
export type StripTeams = {
  homeTpeId?: number | string | null;
  awayTpeId?: number | string | null;
  homeLogo?: string | null;
  awayLogo?: string | null;
  homeShort?: string | null;
  awayShort?: string | null;
};

/** A team's crest, falling back to its short name where there is no image. */
function TeamCrest({ logo, short }: { logo?: string | null; short?: string | null }) {
  if (logo) {
    return (
      <img
        src={logo}
        alt={short ?? ""}
        style={{ width: "24px", height: "24px", objectFit: "contain" }}
      />
    );
  }
  if (short) return <span className="text-slate-500">{short}</span>;
  return null;
}

export function CondensedGameClips({
  clips,
  onSelect,
  currentTime,
  teams,
}: {
  clips: CondensedClip[];
  onSelect: (clip: CondensedClip) => void;
  /** Where the game is now, in seconds. Drives the marked card and the fill. */
  currentTime?: number;
  teams?: StripTeams;
}) {
  const now = Number.isFinite(currentTime as number) ? (currentTime as number) : 0;

  /*
   * The clip the game is currently inside. `start` is the event's own moment
   * and the reel trims a window centred on it, so a clip covers roughly half
   * its duration either side. The exact split lives in segments_settings on
   * the server and is not worth a round trip to decide which card is marked.
   */
  const activeEventId = React.useMemo(() => {
    const hit = clips.find((clip) => {
      if (clip?.start == null) return false;
      const half = Number.isFinite(clip.duration) ? clip.duration / 2 : 0;
      return now >= clip.start - half && now < clip.start + half;
    });
    return hit ? hit.game_event_id : null;
  }, [clips, now]);

  if (!clips.length) return null;

  return (
    <div
      className="flex flex-col border border-slate-200 bg-white"
      style={{ padding: "16px", gap: "12px" }}
    >
      {/*
        The same carousel the Universe rows use, so the arrows behave the way
        they do everywhere else: each one appears only when there is somewhere
        to go in that direction. showViewAll is off because this row already is
        the whole set, so there is nowhere to send anyone.
      */}
      <CarouselSection
        title="Story of the Game"
        showViewAll={false}
        arrowPositionClassName="top-1/2 -translate-y-1/2"
      >
        {clips.map((clip, i) => {
          const isActive = activeEventId === clip.game_event_id;

          /*
           * How far the game has travelled from the previous moment to this
           * one. The connector in the gap fills by it, so the strip shows
           * progress between moments rather than only marking the one in play,
           * and lands full exactly as the next moment is reached.
           */
          const previous = i > 0 ? clips[i - 1] : null;
          const from = previous?.start;
          const to = clip.start;
          const spans = from != null && to != null && to > from;
          const progress = spans
            ? Math.min(1, Math.max(0, (now - from) / (to - from)))
            : 0;

          /*
           * Which crest belongs on which side.
           *
           * The analytics rows carry their own home and away, and nothing
           * guarantees they agree with the tenant's. Matching the clip's TPE
           * team id against the tenant's home_team_id_tpe settles it; when the
           * ids are absent the two are assumed to agree, which is what the
           * score line did before crests existed.
           */
          const sidesAgree =
            clip.home_team_id == null || teams?.homeTpeId == null
              ? true
              : String(clip.home_team_id) === String(teams.homeTpeId);
          const homeSide = {
            logo: teams?.homeLogo,
            short: teams?.homeShort,
            score: (sidesAgree ? clip.home_score : clip.away_score) ?? 0,
          };
          const awaySide = {
            logo: teams?.awayLogo,
            short: teams?.awayShort,
            score: (sidesAgree ? clip.away_score : clip.home_score) ?? 0,
          };
          // Away first, the way this page writes a fixture: "away @ home".
          const left = awaySide;
          const right = homeSide;

          return (
            <button
              key={clip.game_event_id}
              type="button"
              onClick={() => onSelect(clip)}
              className={
                "relative flex shrink-0 flex-col rounded-lg border-2 text-left transition-colors " +
                (isActive ? "bg-slate-50" : "bg-white hover:bg-slate-50")
              }
              style={{
                width: "190px",
                padding: "10px",
                gap: "8px",
                // The tenant's own accent marks the moment in play, so the card
                // reads as highlighted rather than merely a shade darker.
                borderColor: isActive
                  ? "var(--v2-brand-secondary, var(--v2-primary))"
                  : "#e2e8f0",
              }}
              aria-label={`${eventLabel(clip.event_type)}, ${periodLabel(
                clip.period
              )} at ${gameTime(clip.start)}`}
              aria-current={isActive ? "true" : undefined}
            >
              {i > 0 && (
                <span
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    left: `-${CARD_GAP + 2}px`,
                    width: `${CARD_GAP + 2}px`,
                    top: `${BADGE_CENTRE}px`,
                    height: "2px",
                    backgroundColor: "#e2e8f0",
                  }}
                >
                  <span
                    style={{
                      display: "block",
                      height: "100%",
                      width: `${Math.round(progress * 100)}%`,
                      backgroundColor: "var(--v2-primary)",
                      // Matches how often the strip samples playback, so
                      // each tick is animated across rather than jumped.
                      transition: "width 250ms linear",
                    }}
                  />
                  <span
                    style={{
                      position: "absolute",
                      left: "50%",
                      top: "50%",
                      width: "8px",
                      height: "8px",
                      marginLeft: "-4px",
                      marginTop: "-4px",
                      borderRadius: "9999px",
                      backgroundColor:
                        progress >= 1 ? "var(--v2-primary)" : "#cbd5e1",
                      transition: "background-color 250ms linear",
                    }}
                  />
                </span>
              )}

              <div className="flex items-center text-xs font-bold" style={{ gap: "6px" }}>
                <span
                  className="flex items-center justify-center rounded-full text-white"
                  style={{
                    width: "18px",
                    height: "18px",
                    fontSize: "10px",
                    backgroundColor: "var(--v2-primary)",
                  }}
                >
                  {clip.index}
                </span>
                <span style={{ color: "var(--v2-primary)" }}>
                  {eventLabel(clip.event_type)}
                </span>
              </div>

              <div className="text-xs text-slate-500">
                {periodLabel(clip.period)} · at {gameTime(clip.start)}
              </div>

              <div
                className="relative flex items-center justify-center overflow-hidden rounded bg-slate-100"
                style={{ height: "96px" }}
              >
                {clip.thumbnail_url ? (
                  <img
                    src={clip.thumbnail_url}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <Film className="size-6 text-slate-400" aria-hidden="true" />
                )}
                <span
                  className="absolute rounded bg-black/70 text-white"
                  style={{ right: "6px", bottom: "6px", padding: "2px 5px", fontSize: "10px" }}
                >
                  {clipLength(clip.duration)}
                </span>
              </div>

              <div
                className="flex items-center justify-center text-sm font-bold"
                style={{ gap: "10px", color: "var(--v2-primary)" }}
              >
                <TeamCrest logo={left.logo} short={left.short} />
                <span>
                  {left.score} - {right.score}
                </span>
                <TeamCrest logo={right.logo} short={right.short} />
              </div>
            </button>
          );
        })}
      </CarouselSection>

      <div className="text-center text-xs text-slate-500">
        Click any moment to jump to that point in the game.
      </div>
    </div>
  );
}
